import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { CaseDetailPage } from './pages/CaseDetailPage';
import { TriagemPage } from './pages/TriagemPage';
import { ParecerPage } from './pages/ParecerPage';
import { DecisaoPage } from './pages/DecisaoPage';
import { IndicadoresPage } from './pages/IndicadoresPage';
import { RitoBoardPage } from './pages/RitoBoardPage';
import QRCode from 'qrcode';
import {
  getStoredSession,
  logout,
  acceptPolicy,
  changePassword,
  activateFirstAccess,
} from './lib/auth-client';
import { LGP_02_VERSION } from '@aions/core';
import {
  Alert,
  Brand,
  Button,
  StatusDot,
  ErrorBoundary,
  PolicyAcceptanceModal,
  ChangePasswordForm,
  CHANGE_PASSWORD_MIN_LENGTH,
  FirstAccessForm,
  FIRST_ACCESS_MIN_LENGTH,
  SessionCountdown,
} from '@aions/ui-primitives';
import { FirstAccessFaceStep } from './components/FirstAccessFaceStep';
import './styles/global.css';

type Route =
  | { kind: 'login' }
  | { kind: 'first-access'; token: string; email: string; otpAuthUri: string | undefined }
  | { kind: 'dashboard' }
  | { kind: 'case'; id: string }
  | { kind: 'triagem'; id: string }
  | { kind: 'parecer'; id: string }
  | { kind: 'decisao'; id: string }
  | { kind: 'indicadores' }
  | { kind: 'rito-board' }
  | { kind: 'change-password' };

/** Parses #first-access?token=...&email=...&otp=... into a Route. */
function parseHashRoute(): Route | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (hash.startsWith('first-access')) {
    const qIdx = hash.indexOf('?');
    if (qIdx === -1) return null;
    const params = new URLSearchParams(hash.slice(qIdx + 1));
    const token = params.get('token');
    const email = params.get('email');
    const otp = params.get('otp');
    if (!token || !email) return null;
    return { kind: 'first-access', token, email, otpAuthUri: otp === null ? undefined : otp };
  }
  return null;
}

function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const { session, setSession, needsReauth } = useSession();

  const [route, setRoute] = useState<Route>(() => {
    const hashRoute = parseHashRoute();
    if (hashRoute) return hashRoute;
    return session ? { kind: 'dashboard' } : { kind: 'login' };
  });

  // Foto da cerimônia de primeiro acesso (ADR-0022). Vive SÓ na memória desta aba, entre
  // a captura e o POST único que grava senha e template juntos. `null` = ainda no passo
  // do rosto; uma falha do serviço de biometria devolve a este estado, porque nada foi
  // gravado e o link continua valendo.
  const [fotoCerimonia, setFotoCerimonia] = useState<Blob | null>(null);
  // Erro do serviço de biometria, exibido de volta NO PASSO DO ROSTO. Sem isto ele caía no
  // `errGeneric` do formulário — "Token inválido ou expirado. Solicite novo link ao admin." —
  // que é falso e manda a pessoa pedir link novo quando o link está perfeito e o que falhou
  // foi a foto. Mediu-se em 24/08: a mensagem certa é a diferença entre "refaça a captura" e
  // "chame a TI".
  const [erroRosto, setErroRosto] = useState<string | null>(null);

  // Sprint 3.1 — bloqueia tudo até user aceitar LGP-02 (versão atual).
  // session.user.policyAccepted é setado por /login; bump da versão do server
  // invalida o flag e o middleware backend devolve 403 — duplo gate.
  // Regra 2 — com `needsReauth` a sessão está morta e o aceite devolveria 401. O
  // modal é bloqueante: exibi-lo aqui esconderia o aviso de reautenticação atrás de
  // uma ação impossível. Antes da regra 2 isto não acontecia porque o 401 zerava a
  // sessão e o modal sumia junto; agora a sessão sobrevive, então o gate é explícito.
  const needsPolicyAccept =
    session !== null &&
    !needsReauth &&
    session.user.policyAccepted === false &&
    route.kind !== 'login' &&
    route.kind !== 'first-access';

  useEffect(() => {
    const onHash = (): void => {
      // Regra 2 — segundo caminho de navegação, bloqueado por guard PRÓPRIO (não
      // pelo `disabled` dos botões): trocar de rota desmonta a página e destrói o
      // texto em curso. Hoje `parseHashRoute` só resolve `#first-access`, mas uma
      // rota hash nova tornaria este caminho corriqueiro sem ninguém lembrar deste
      // comentário — proteção não nasce com desvio conhecido. As duas defesas são
      // independentes de propósito: remover uma NÃO pode ser mascarado pela outra
      // (cada uma tem seu teste; ver App.reauth.test.tsx).
      if (needsReauth) return;
      const hashRoute = parseHashRoute();
      if (hashRoute) setRoute(hashRoute);
    };
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
    };
    // `needsReauth` nas deps: sem isto o listener fecharia sobre o valor da 1ª
    // render (false) e o guard nunca veria a sessão morta — furo silencioso.
  }, [needsReauth]);

  // If session is dropped (logout), force back to login (unless on first-access).
  if (!session && route.kind !== 'login' && route.kind !== 'first-access') {
    setRoute({ kind: 'login' });
  }

  return (
    <div className="app">
      <a className="skip-to-main" href="#main-content">
        {t('app.skip_to_main')}
      </a>
      <header className="app-header">
        <Brand size="md" tagline={t('app.header_tagline')} />
        <StatusDot tone="brand" label={t('app.header_scope')} />
        {/* Contador de expiração da sessão. Só com sessão viva — nas telas de
            login/primeiro-acesso não há o que contar, e um contador ali
            sugeriria que a sessão já começou. */}
        {session && (
          <SessionCountdown
            token={session.token}
            label={t('app.session_expires_in')}
            expiredLabel={t('app.session_expired')}
          />
        )}
        {session && route.kind !== 'login' && route.kind !== 'first-access' && (
          <>
            {/* Regra 2 — com a sessão morta, navegar DESMONTA a página e destrói o
                texto em curso, pela mesma porta que a regra 2 acabou de fechar. E
                não tira nada: qualquer outra tela daria 401 do mesmo jeito. O
                travamento impede um clique que ia falhar de todo jeito e levar o
                trabalho junto. `disabled` do Button dá estado VISÍVEL (opacity .5 +
                cursor not-allowed) — controle que parece clicável e não responde faz
                o membro achar que travou e apertar F5, que é o bug de origem. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRoute({ kind: 'rito-board' })}
              data-testid="nav-rito-board"
              disabled={needsReauth}
              title={needsReauth ? t('app.nav_bloqueada_reauth') : undefined}
              style={{ marginLeft: 'auto' }}
            >
              Rito
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRoute({ kind: 'indicadores' })}
              data-testid="nav-indicadores"
              disabled={needsReauth}
              title={needsReauth ? t('app.nav_bloqueada_reauth') : undefined}
            >
              Indicadores
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRoute({ kind: 'change-password' })}
              data-testid="nav-change-password"
              disabled={needsReauth}
              title={needsReauth ? t('app.nav_bloqueada_reauth') : undefined}
            >
              {t('app.change_password')}
            </Button>
          </>
        )}
      </header>

      {/* Regra 2 — aviso PERSISTENTE (não toast) sobre a página VIVA. É o que
          impede o limbo: `needsReauth` sem nada renderizado deixaria a tela muda
          com botões travados. A saída é manual e explícita; nunca navegamos
          sozinhos daqui, porque navegar destrói o texto em curso. */}
      {needsReauth && session && (
        <Alert
          variant="warning"
          role="status"
          aria-live="polite"
          title={t('app.reauth_title')}
          style={{ margin: '12px 16px 0' }}
        >
          <div>{t('app.reauth_body')}</div>
          <Button
            variant="primary"
            size="sm"
            data-testid="reauth-entrar"
            style={{ marginTop: 8 }}
            onClick={() => {
              void (async () => {
                await logout();
                setSession(null);
              })();
            }}
          >
            {t('app.reauth_acao')}
          </Button>
        </Alert>
      )}

      <main id="main-content" className="app-main" tabIndex={-1}>
        <ErrorBoundary
          title={t('error_boundary.title')}
          message={t('error_boundary.message')}
          reloadLabel={t('error_boundary.reload')}
        >
          {route.kind === 'login' && (
            <LoginPage onSuccess={() => setRoute({ kind: 'dashboard' })} />
          )}
          {/* CERIMÔNIA ATÔMICA (ADR-0022) — o rosto vem ANTES da senha e do TOTP, e o
              envio é um POST só. A ordem não é estética: o código do autenticador vive
              30 s e o servidor tranca a conta em 5 erros de TOTP; pedir o código antes de
              a pessoa resolver permissão de câmera e enquadramento faria o código expirar
              na mão dela, repetidamente, dentro da própria cerimônia.
              A foto fica só na memória desta aba até o envio. */}
          {route.kind === 'first-access' && fotoCerimonia === null && (
            <FirstAccessFaceStep
              onCaptured={(foto) => {
                setErroRosto(null);
                setFotoCerimonia(foto);
              }}
              erroExterno={erroRosto}
            />
          )}
          {route.kind === 'first-access' && fotoCerimonia !== null && (
            <FirstAccessForm
              email={route.email}
              otpAuthUri={route.otpAuthUri}
              minLength={FIRST_ACCESS_MIN_LENGTH}
              devDefaultTotp={import.meta.env.MODE === 'development' ? '000000' : undefined}
              makeQrSvg={(uri) => QRCode.toString(uri, { type: 'svg', margin: 1 })}
              labels={{
                title: t('first_access.title'),
                intro: t('first_access.intro'),
                emailPrefix: t('first_access.email_prefix'),
                qrUnavailable: t('first_access.qr_unavailable'),
                qrNotProvided: t('first_access.qr_not_provided'),
                otpCopyLabel: t('first_access.otp_copy_label'),
                passwordLabel: t('first_access.password_label', { min: FIRST_ACCESS_MIN_LENGTH }),
                confirmLabel: t('first_access.confirm_label'),
                totpLabel: t('first_access.totp_label'),
                submit: t('first_access.submit'),
                submitting: t('first_access.submitting'),
                errTooShort: t('first_access.errors.too_short', { min: FIRST_ACCESS_MIN_LENGTH }),
                errMismatch: t('first_access.errors.mismatch'),
                errInvalidTotp: t('first_access.errors.invalid_totp'),
                errTotpIncorrect: t('first_access.errors.totp_incorrect'),
                errAlreadyActivated: t('first_access.errors.already_activated'),
                errNetwork: t('first_access.errors.network'),
                errGeneric: t('first_access.errors.generic'),
              }}
              onSubmit={async (password, totpCode) => {
                const res = await activateFirstAccess(
                  route.token,
                  route.email,
                  password,
                  totpCode,
                  fotoCerimonia,
                );
                if (res.ok) return { ok: true };
                if (res.error === 'invalid_totp') return { ok: false, error: 'invalid_totp' };
                if (res.error === 'already_activated')
                  return { ok: false, error: 'already_activated' };
                if (res.error === 'network') return { ok: false, error: 'network' };
                // Falha do serviço de biometria: NADA foi gravado e o link continua
                // válido. Devolver a pessoa ao passo do rosto é a ação certa — e é o
                // único caminho de recuperação que não passa pela TI.
                if (
                  res.error?.startsWith('liveness_') === true ||
                  res.error === 'foto_obrigatoria'
                ) {
                  // Volta ao passo do rosto COM a mensagem certa. `defaultValue` porque um
                  // código novo do serviço não pode virar chave crua na tela.
                  setErroRosto(
                    t(`first_access.errors.${res.error}`, {
                      defaultValue: t('first_access.rosto.erro_captura'),
                    }),
                  );
                  setFotoCerimonia(null);
                  return { ok: false, error: 'other' };
                }
                return { ok: false, error: 'other' };
              }}
              onSuccess={() => {
                window.location.hash = '';
                setRoute({ kind: 'login' });
              }}
            />
          )}
          {route.kind === 'dashboard' && (
            <DashboardPage
              onSelectCase={(id) => setRoute({ kind: 'case', id })}
              onLogout={() => setRoute({ kind: 'login' })}
            />
          )}
          {route.kind === 'case' && (
            <CaseDetailPage
              caseId={route.id}
              onBack={() => setRoute({ kind: 'dashboard' })}
              onTriagem={(id) => setRoute({ kind: 'triagem', id })}
              onParecer={(id) => setRoute({ kind: 'parecer', id })}
              onDecisao={(id) => setRoute({ kind: 'decisao', id })}
            />
          )}
          {route.kind === 'triagem' && (
            <TriagemPage
              caseId={route.id}
              onBack={() => setRoute({ kind: 'case', id: route.id })}
              onDone={() => setRoute({ kind: 'case', id: route.id })}
            />
          )}
          {route.kind === 'parecer' && (
            <ParecerPage
              caseId={route.id}
              onBack={() => setRoute({ kind: 'case', id: route.id })}
              onDone={() => setRoute({ kind: 'case', id: route.id })}
            />
          )}
          {route.kind === 'decisao' && (
            <DecisaoPage
              caseId={route.id}
              onBack={() => setRoute({ kind: 'case', id: route.id })}
              onDone={() => setRoute({ kind: 'case', id: route.id })}
            />
          )}
          {route.kind === 'indicadores' && (
            <IndicadoresPage onBack={() => setRoute({ kind: 'dashboard' })} />
          )}
          {route.kind === 'rito-board' && (
            <RitoBoardPage
              onBack={() => setRoute({ kind: 'dashboard' })}
              onSelectCase={(id) => setRoute({ kind: 'case', id })}
            />
          )}
          {route.kind === 'change-password' && (
            <ChangePasswordForm
              minLength={CHANGE_PASSWORD_MIN_LENGTH}
              labels={{
                title: t('app.change_password'),
                intro: t('change_password.intro'),
                currentLabel: t('change_password.current_label'),
                newLabel: t('change_password.new_label', { min: CHANGE_PASSWORD_MIN_LENGTH }),
                confirmLabel: t('change_password.confirm_label'),
                totpLabel: t('change_password.totp_label'),
                submit: t('change_password.submit'),
                submitting: t('change_password.submitting'),
                cancel: t('change_password.cancel'),
                doneTitle: t('change_password.done_title'),
                doneMessage: t('change_password.done_message'),
                doneButton: t('change_password.done_button'),
                errTooShort: t('change_password.errors.too_short', {
                  min: CHANGE_PASSWORD_MIN_LENGTH,
                }),
                errMismatch: t('change_password.errors.mismatch'),
                errSameAsCurrent: t('change_password.errors.same_password'),
                errInvalidTotp: t('change_password.errors.invalid_totp'),
                errInvalidCredentials: t('change_password.errors.invalid_credentials'),
                errNotAuthenticated: t('change_password.errors.not_authenticated'),
                errRecarregarPagina: t('change_password.errors.recarregar_pagina'),
                errGeneric: t('change_password.errors.generic'),
              }}
              onSubmit={async (currentPassword, newPassword, totpCode) => {
                const res = await changePassword(currentPassword, newPassword, totpCode);
                if (res.ok) return { ok: true };
                if (res.error === 'invalid_credentials')
                  return { ok: false, error: 'invalid_credentials' };
                if (res.error === 'same_password') return { ok: false, error: 'same_password' };
                if (res.error === 'not_authenticated')
                  return { ok: false, error: 'not_authenticated' };
                // 400 do servidor (bundle antigo sem o re-embrulho) e 404 do
                // my-keypair (leitura desabilitada) tem a MESMA acao do lado do
                // membro: recarregar a pagina. Codigo cru na tela nao ajuda ninguem.
                if (
                  res.error === 'reembrulho_obrigatorio' ||
                  res.error === 'reembrulho_inesperado' ||
                  res.error === 'leitura_desabilitada' ||
                  res.error === 'reembrulho_falhou' ||
                  res.error === 'keypair_indisponivel'
                )
                  return { ok: false, error: 'recarregar_pagina' };
                return { ok: false, error: 'other' };
              }}
              onDone={() => setRoute({ kind: 'dashboard' })}
              onCancel={() => setRoute({ kind: 'dashboard' })}
            />
          )}
        </ErrorBoundary>
      </main>

      {needsPolicyAccept && (
        <PolicyAcceptanceModal
          version={LGP_02_VERSION}
          labels={{
            title: t('policy_modal.title'),
            subtitle: t('policy_modal.subtitle'),
            versionLabel: t('policy_modal.version_label'),
            draftWarning: t('policy_modal.draft_warning'),
            scrollHint: t('policy_modal.scroll_hint'),
            checkbox: t('policy_modal.checkbox'),
            acceptButton: t('policy_modal.accept_button'),
            accepting: t('policy_modal.accepting'),
            logoutButton: t('policy_modal.logout_button'),
            errorNetwork: t('policy_modal.errors.network'),
            errorSubmit: t('policy_modal.errors.submit_failed'),
          }}
          onAccept={async () => {
            const res = await acceptPolicy();
            return res.ok
              ? { ok: true }
              : { ok: false, error: res.error === 'network' ? 'network' : 'submit' };
          }}
          onAccepted={() => {
            // Sync com sessionStorage atualizado por acceptPolicy().
            setSession(getStoredSession());
          }}
          onLogout={async () => {
            await logout();
            setSession(null);
            setRoute({ kind: 'login' });
          }}
        />
      )}
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <SessionProvider>
      <AppShell />
    </SessionProvider>
  );
}
